<template>
  <div class="listing-list__wrap">
    <ListingBreadcrumbs
      :categories="categories"
      :subcategories="subcategories"
      :pageHandle="pageHandle"
      :pageTitle="introSection.sectionTitle"
    />
    <div class="listing-list__main m-bot-1 row-2">
      <!-- Filters Overlay Mobile -->
      <WidgetOverlayFullScreen class="filters-overlay" ref="overlay">
        <div class="filters-overlay__content" data-lenis-prevent>
          <div class="intro">
            <p class="menu-text" v-html="$t('common.filtri')" />
            <div class="close-button" @click="toggleOverlay">
              <CloseFiltersIcon />
            </div>
          </div>
          <FiltersAside
            v-if="!matchedLarge && !hideSidebar"
            :filters="facets"
            :staticFilters="staticFacets"
            @update:filters="onUpdateFilters"
          />
        </div>
      </WidgetOverlayFullScreen>
      <div class="listing-list__aside">
        <div class="aside-content" data-lenis-prevent>
          <ListingCategories
            :pageHandle="pageHandle"
            :categories="categories"
            :subcategories="subcategories"
            :facetFilters="facetFilters"
            :openedCategory="openedCategory"
            :defaultSelected="currentCustomContent?.key"
          />
          <FiltersAside
            v-if="matchedLarge && !hideSidebar"
            :filters="facets"
            :staticFilters="staticFacets"
            @update:filters="onUpdateFilters"
            class="show-for-large"
          />
          <!-- Filters Button for Mobile -->
          <div class="filters-button-container" v-if="!hideSidebar">
            <button
              class="filters-button hide-for-large"
              @click="toggleOverlay"
            >
              <span class="text-medium" v-html="$t('common.filtri')" />
              <FilterIcon />
            </button>
          </div>
        </div>
      </div>
      <div class="listing-list__products">
        <!-- Per l'immagine unica nell'intro, presa dalla famiglia generale (Vino, Birra) :image="parentIntroSectionImage" -->
        <ListingIntro
          :introSection="introSection"
          v-if="!hideListing"
          class="show-for-large"
        />
        <ListingProducts :items="hits" v-if="!hideListing" />
        <ListingPagination :pages="nbPages" v-if="!hideListing" />
        <template v-if="currentCustomContent">
          <slot />
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import CloseFiltersIcon from '@/assets/img/ico/icon_close_filter.svg'
import FilterIcon from '@/assets/img/ico/icon_filter.svg'
import { transformFacets, parseQueryFacets } from '~/assets/js/utils/algolia.js'
import gsap from 'gsap'

const props = defineProps({
  indexes: { type: Array, default: () => [] },
  defaultFacetsFilters: { type: Array, default: () => [] },
  pageHandle: { type: String, default: '' },
  // pageTitle: { type: String, default: '' },
  categories: { type: Array, default: () => [] },
  subcategories: { type: Object, default: () => ({}) },
  introSection: { type: Object, default: () => ({}) },
  parentIntroSectionImage: { type: Object, default: () => ({}) },
  hideSidebar: {
    type: Boolean,
    default: false,
  },
  hideListing: {
    type: Boolean,
    default: false,
  },
  currentCustomContent: { type: Object, default: null },
})

// Router e route per ottenere informazioni sulla navigazione corrente
const router = useRouter()
const route = useRoute()

// Overlay
const overlay = ref(null)
const openOverlay = ref(false)

// Gsap timeline
const tl = ref(null)

// Stato per la categoria aperta
const openedCategory = ref(null)

// Stato per i filtri dei facet
const facetFilters = ref(
  route.query?.filters ? parseQueryFacets(route.query?.filters) : [],
)
// Stato per la query di ricerca
const query = defineModel('query', { default: null })

// Computed per ottenere la pagina corrente
const page = computed(() => (route.query?.page > 0 ? route.query?.page : 1))

// Computed per costruire le richieste di ricerca
const searchRequests = computed(() => {
  return props.indexes.reduce(
    (acc, indexName) => [
      ...acc,
      {
        indexName,
        query: query.value ?? '*',
        hitsPerPage: 20,
        page: page.value - 1,
        facets: ['*'],
        facetFilters: [...props.defaultFacetsFilters, ...facetFilters.value],
      },
    ],
    [],
  )
})

// Esegue la ricerca su Algolia e ottiene i risultati
const { hits, facets, staticFacets, nbPages } = await useAlgoliaListing({
  requests: searchRequests,
})

// Funzione per aggiornare i filtri
const onUpdateFilters = (filters) => {
  facetFilters.value = transformFacets(filters.selectedFilters)

  router.push({
    query: {
      ...route.query,
      filters: facetFilters.value,
      page: 1,
    },
  })
}

// Gestisco Overlay
const toggleOverlay = () => {
  if (tl.value?.isActive()) {
    tl.value.progress(1)
  }
  openOverlay.value = !openOverlay.value
}
watch(
  () => openOverlay.value,
  (openOverlay) => {
    tl.value = openOverlay
      ? animateOverlayIn().duration(0.7)
      : animateOverlayOut().duration(0.7)
    tl.value.play()
  },
)
watch(
  () => route.path,
  () => {
    if (openOverlay.value) {
      toggleOverlay()
    }
  },
)

// Animazioni Overlay
const animateOverlayIn = () => {
  const tl = gsap.timeline({
    paused: true,
    onComplete: () => {
      tl.kill()
    },
  })

  if (overlay.value) {
    tl.add(overlay.value.animateIn().play(), '<')
  }
  return tl
}
const animateOverlayOut = () => {
  const tl = gsap.timeline({
    paused: true,
    onComplete: () => {
      tl.kill()
    },
  })
  if (overlay.value) {
    tl.add(overlay.value.animateOut().play(), '<')
  }

  return tl
}

const matchedLarge = ref(true)

onMounted(() => {
  checkMedia()
  window.addEventListener('resize', checkMedia)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMedia)
})

const checkMedia = () => {
  const check = window.matchMedia('(min-width: 999px)')
  matchedLarge.value = check.matches
}
</script>

<script>
export default {
  name: 'ListingList',
}
</script>

<style lang="scss">
@import './style.scss';
</style>
